<script setup lang="ts">
import type { ResolvedBreadcrumb } from '~/composables/useBreadcrumbs'

defineProps<{
  items: ResolvedBreadcrumb[]
}>()

const { t } = useI18n()
</script>

<template>
  <!-- Deliberately not shown in the UI. `sr-only` keeps the trail in the DOM and
       in the accessibility tree, so it stays real content backing the
       BreadcrumbList JSON-LD that `useBreadcrumbs` emits, while taking up no
       layout. Markup is kept semantic but unstyled — nothing here is ever
       painted, so visual classes and the separator icon would be dead weight.

       A single crumb is not a trail; `useBreadcrumbs` skips the JSON-LD in that
       case too, so the two stay in step. -->
  <nav v-if="items.length > 1" :aria-label="t('label.breadcrumb')" class="sr-only">
    <ol>
      <li v-for="(crumb, index) in items" :key="index">
        <!-- `tabindex="-1"` keeps these out of the sequential tab order: they
             are invisible, so focusing them would strand a sighted keyboard
             user with no focus ring (WCAG 2.4.7). Nothing is lost — every
             destination is also reachable from the visible navigation, and
             screen readers still expose these via browse mode / the link
             rotor, which is where the trail earns its keep. -->
        <NuxtLink v-if="crumb.to" :to="crumb.to" tabindex="-1">{{ crumb.label }}</NuxtLink>
        <span v-else aria-current="page">{{ crumb.label }}</span>
      </li>
    </ol>
  </nav>
</template>
